import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSeo } from "../lib/seo";
import { Field } from "../components/Common";
import { LogoMark } from "../components/Logo";
import { IMAGES } from "../lib/constants";

export default function Login() {
  useSeo("Investor Portal Sign In", "Sign in to the Sido Holdings investor portal.");
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", company: "" });
  const [busy, setBusy] = useState(false);
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", mode === "login" ? { email: f.email, password: f.password } : f);
      signIn(data);
      toast.success(`Welcome${data.user.name ? `, ${data.user.name.split(" ")[0]}` : ""}`);
      nav(params.get("next") || (data.user.role === "admin" ? "/admin" : "/dashboard"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <section className="min-h-screen grid lg:grid-cols-2 bg-paper">
      <div className="relative hidden lg:block bg-navy overflow-hidden">
        <img src={IMAGES.night} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute bottom-16 left-16 right-16 text-paper">
          <LogoMark tone="light" className="h-12 w-12" />
          <p className="mt-6 font-serif text-4xl leading-tight">Your capital. Your criteria.<br />Our local legwork.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-5 pt-28 pb-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="auth-form">
          <p className="eyebrow">Investor Portal</p>
          <h1 className="text-4xl font-medium text-navy">{mode === "login" ? "Sign in" : "Create your account"}</h1>
          {mode === "register" && (
            <>
              <Field label="Full name"><input required className="field" value={f.name} onChange={set("name")} data-testid="auth-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone"><input className="field" type="tel" value={f.phone} onChange={set("phone")} data-testid="auth-phone" /></Field>
                <Field label="Company"><input className="field" value={f.company} onChange={set("company")} data-testid="auth-company" /></Field>
              </div>
            </>
          )}
          <Field label="Email"><input required type="email" className="field" value={f.email} onChange={set("email")} autoComplete="email" data-testid="auth-email" /></Field>
          <Field label="Password" hint={mode === "register" ? "Minimum 8 characters" : null}><input required type="password" minLength={mode === "register" ? 8 : undefined} className="field" value={f.password} onChange={set("password")} autoComplete={mode === "login" ? "current-password" : "new-password"} data-testid="auth-password" /></Field>
          <button disabled={busy} className="btn-primary w-full" data-testid="auth-submit">{busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}</button>
          <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-sm text-mist hover:text-navy" data-testid="auth-toggle-mode">
            {mode === "login" ? "New investor? Create an account" : "Already have an account? Sign in"}
          </button>
          <p className="text-xs text-mist text-center">Prefer not to create an account? <Link to="/investors#criteria" className="underline">Submit your criteria</Link> directly.</p>
        </form>
      </div>
    </section>
  );
}
